import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { mmsLogin, mmsVerifyOtp, mmsValidateSession } from '../../../../services/mmsApi';
import { API_BASE_URL } from '../../../../config';

const MMSMobileLogin = () => {
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [validatingSession, setValidatingSession] = useState(true);
  
  // OTP state
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpToken, setOtpToken] = useState('');
  const [otpMessage, setOtpMessage] = useState('');

  // Check if already logged in
  useEffect(() => {
    const validateExistingSession = async () => {
      const token = localStorage.getItem('mms_mobile_token');
      const userData = localStorage.getItem('mms_mobile_user');
      
      console.log('🔍 MMS Mobile Login - Auth check:', { 
        hasToken: !!token, 
        hasUserData: !!userData,
        currentPath: window.location.pathname 
      });
      
      if (token && userData) {
        try {
          // Validate the token with the backend
          console.log('🔐 Validating existing session...');
          const response = await mmsValidateSession();
          
          if (response.data.status === 1 && response.data.data) {
            // Session is valid - update stored user data and redirect
            console.log('✅ MMS Mobile Login - Valid session confirmed by server');
            localStorage.setItem('mms_mobile_user', JSON.stringify(response.data.data.worker));
            navigate('/mms-mobile/jobs');
            return;
          }
        } catch (error) {
          console.error('❌ MMS Mobile Login - Session validation failed:', error);
          // Clear invalid session data
          localStorage.removeItem('mms_mobile_token');
          localStorage.removeItem('mms_mobile_user');
        }
      }
      
      setValidatingSession(false);
      loadSettings();
    };
    
    validateExistingSession();
  }, [navigate]);

  const loadSettings = async () => {
    try {
      // Fetch settings directly without authentication (public endpoint)
      const response = await fetch(`${API_BASE_URL}/api/settings`);
      const data = await response.json();
      if (data && data.status === 1 && data.data) {
        setSettings(data.data);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      // Silently fail - logo will fall back to MMS text
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!phone || !password) {
      toast.error('Please enter phone number and password');
      return;
    }

    setLoading(true);
    try {
      const response = await mmsLogin(phone, password);
      
      // Check if OTP is required
      if (response.data.status === 'otp_required') {
        setOtpToken(response.data.otp_token);
        setOtpMessage(response.data.message);
        setShowOtpInput(true);
        toast.success('OTP sent! Check your phone.');
      } else if (response.data.success && response.data.data) {
        // Legacy path - direct login without OTP (shouldn't happen with new flow)
        localStorage.setItem('mms_mobile_token', response.data.data.token);
        localStorage.setItem('mms_mobile_user', JSON.stringify(response.data.data.worker));
        toast.success('Login successful!');
        navigate('/mms-mobile/jobs');
      } else {
        throw new Error(response.data.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.message || error.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    
    if (!otp) {
      toast.error('Please enter the OTP code');
      return;
    }

    setLoading(true);
    try {
      console.log('🔐 Verifying OTP with token:', otpToken);
      const response = await mmsVerifyOtp(otp, otpToken);
      
      console.log('📥 OTP Response:', response.data);
      console.log('📊 Response structure:', {
        status: response.data.status,
        hasData: !!response.data.data,
        hasWorker: !!(response.data.data && response.data.data.worker),
        hasToken: !!(response.data.data && response.data.data.token)
      });
      
      // ApiMMS returns status: 1 for success with worker data
      if (response.data.status === 1 && response.data.data && response.data.data.worker) {
        console.log('✅ OTP verified, storing token and worker data');
        
        const token = response.data.data.token;
        const worker = response.data.data.worker;
        
        if (!token || !worker) {
          console.error('❌ Missing token or worker data in response');
          throw new Error('Invalid response: missing authentication data');
        }
        
        try {
          // Store data with verification
          localStorage.setItem('mms_mobile_token', token);
          localStorage.setItem('mms_mobile_user', JSON.stringify(worker));
          
          // Verify data was stored correctly
          const storedToken = localStorage.getItem('mms_mobile_token');
          const storedUser = localStorage.getItem('mms_mobile_user');
          
          if (!storedToken || !storedUser) {
            throw new Error('Failed to store authentication data - localStorage may be disabled');
          }
          
          console.log('✅ Authentication data stored successfully');
          toast.success('Login successful!');
          navigate('/mms-mobile/jobs');
        } catch (storageError) {
          console.error('❌ LocalStorage error:', storageError);
          throw new Error('Failed to save login data. Please check your browser settings and try again.');
        }
      } else {
        console.error('❌ Unexpected response structure:', response.data);
        throw new Error(response.data.message || 'OTP verification failed');
      }
    } catch (error) {
      console.error('❌ OTP verification error:', error);
      console.error('❌ Error response:', error.response?.data);
      toast.error(error.response?.data?.message || error.message || 'Invalid OTP');
      setOtp(''); // Clear OTP input on error
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowOtpInput(false);
    setOtp('');
    setOtpToken('');
    setOtpMessage('');
  };

  // Show loading spinner while validating session
  if (validatingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F7FA' }}>
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Checking session...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F5F7FA' }}>
      {/* Logo Section - White */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="text-center">
          <div className="w-64 h-40 mx-auto mb-4 flex items-center justify-center">
            {settings?.site_logo_preview && settings.site_logo_preview !== 'Not available' ? (
              <img 
                src={settings.site_logo_preview} 
                alt="Logo" 
                className="w-full h-full object-contain" 
              />
            ) : (
              <svg className="w-full h-full" fill="none" viewBox="0 0 200 100">
                <text x="100" y="55" textAnchor="middle" style={{ 
                  fontSize: '56px', 
                  fontWeight: '800', 
                  fill: '#0099E0',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  letterSpacing: '4px'
                }}>
                  MMS
                </text>
              </svg>
            )}
          </div>
        </div>
      </div>

      {/* Form Section - Blue Gradient with Rounded Top */}
      <div 
        className="rounded-t-[40px] px-6 pt-12 pb-16 min-h-[60vh]"
        style={{ 
          background: 'linear-gradient(180deg, #0099E0 0%, #0099E0 50%, #0086D4 100%)',
          boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.1)'
        }}
      >
        <div className="max-w-md mx-auto">
          <h1 className="text-5xl font-extrabold text-white text-center mb-2" style={{ letterSpacing: '6px', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
            MMS
          </h1>
          <p className="text-white text-center mb-8 font-medium opacity-90">
            Maintenance Management System
          </p>

          {!showOtpInput ? (
            // Login Form
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Phone Number"
                  disabled={loading}
                  className="w-full px-4 py-3.5 bg-white bg-opacity-95 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 disabled:opacity-60 text-gray-800 placeholder-gray-500"
                  style={{ boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)', height: '52px' }}
                  autoComplete="tel"
                  inputMode="numeric"
                />
              </div>

              <div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  disabled={loading}
                  className="w-full px-4 py-3.5 bg-white bg-opacity-95 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 disabled:opacity-60 text-gray-800 placeholder-gray-500"
                  style={{ boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)', height: '52px' }}
                  autoComplete="current-password"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 bg-white rounded-xl font-bold text-lg disabled:opacity-60 transition-all hover:scale-[1.02] active:scale-[0.98]"
                style={{ 
                  color: '#0099E0',
                  boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
                  height: '52px'
                }}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-3 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>
          ) : (
            // OTP Form
            <form onSubmit={handleVerifyOtp} className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-white mb-2">Verification Code</h2>
                <p className="text-white text-opacity-95 text-sm leading-5">
                  {otpMessage || 'Enter the code sent to your phone'}
                </p>
              </div>

              <div>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="00000"
                  maxLength="5"
                  disabled={loading}
                  className="w-full px-4 py-4 bg-white bg-opacity-95 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 text-center text-4xl font-bold tracking-[12px] disabled:opacity-60"
                  style={{ 
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    height: '64px',
                    color: '#0099E0'
                  }}
                  autoComplete="one-time-code"
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={loading || otp.length < 5}
                className="w-full py-3.5 bg-white rounded-xl font-bold text-lg disabled:opacity-60 transition-all hover:scale-[1.02] active:scale-[0.98]"
                style={{ 
                  color: '#0099E0',
                  boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
                  height: '52px'
                }}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-3 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : (
                  'Verify & Continue'
                )}
              </button>

              <button
                type="button"
                onClick={handleBackToLogin}
                disabled={loading}
                className="w-full py-3 text-white font-semibold hover:bg-white hover:bg-opacity-10 rounded-xl transition-all disabled:opacity-60"
              >
                ← Back to Login
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default MMSMobileLogin;
